import { useState } from "react";
import { toast } from "sonner";
import { Share2, MessageSquare, Copy, Download, Smartphone, Loader2 } from "lucide-react";
import { apiError, fileUrl } from "@/lib/api";
import { chatApi, getChatToken } from "@/lib/chatSession";
import { ChatPinGate } from "@/components/chat/ChatPinGate";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const shareText = (s, branchName) => [
  `TX ID / UTR: ${s.transaction_id || s.utr || "-"}`,
  `Amount: ₹${s.transaction_amount ?? s.amount ?? s.request_amount ?? "-"}`,
  `Branch: ${branchName || s.branch_name || "-"}`,
  `Date: ${s.submitted_at ? new Date(s.submitted_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}`,
].join("\n");

async function imageFile(s) {
  const r = await fetch(fileUrl(s.screenshot_url));
  const b = await r.blob();
  const ext = (b.type.split("/")[1] || "png").replace("jpeg", "jpg");
  return new File([b], `payment-${s.transaction_id || s.id}.${ext}`, { type: b.type || "image/png" });
}
function download(file) {
  const u = URL.createObjectURL(file); const a = document.createElement("a");
  a.href = u; a.download = file.name; a.click(); setTimeout(() => URL.revokeObjectURL(u), 1000);
}

function SendToChat({ s, text, onClose }) {
  const [unlocked, setUnlocked] = useState(!!getChatToken());
  const [convs, setConvs] = useState(null); const [to, setTo] = useState(""); const [busy, setBusy] = useState(false);
  const load = () => chatApi("get", "/chat/conversations").then((r) => { setConvs(r.data); if (r.data.length === 1) setTo(r.data[0].branch_id); }).catch(() => setConvs([]));
  if (unlocked && convs === null) load();
  const send = async () => {
    setBusy(true);
    try { await chatApi("post", "/chat/share-submission", { branch_id: to, submission_id: s.id, text }); toast.success("Sent to chat"); onClose(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="share-chat-dialog">
        <DialogHeader><DialogTitle>Send to Chat</DialogTitle></DialogHeader>
        {!unlocked ? <ChatPinGate compact onUnlocked={() => { setUnlocked(true); }} /> : (
          <div className="space-y-3">
            {convs === null ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <select data-testid="share-chat-conversation" value={to} onChange={(e) => setTo(e.target.value)} className="w-full h-10 rounded-md border border-slate-300 bg-white px-2 text-sm">
                <option value="">Choose conversation</option>
                {convs.map((c) => <option key={c.branch_id} value={c.branch_id}>{c.title}</option>)}
              </select>)}
            <img src={fileUrl(s.screenshot_url)} alt="screenshot" className="max-h-40 rounded border border-slate-200" />
            <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap">{text}</pre>
            <Button data-testid="share-chat-send" disabled={!to || busy} onClick={send} className="w-full bg-teal-600 hover:bg-teal-700 text-white">{busy ? "Sending..." : "Send"}</Button>
          </div>)}
      </DialogContent>
    </Dialog>
  );
}

export function PayinShare({ s, branchName }) {
  const [chat, setChat] = useState(false);
  if (!s.screenshot_url) return null;
  const text = shareText(s, branchName);
  const native = async (preferWhatsApp) => {
    try {
      const file = await imageFile(s);
      if (navigator.canShare && navigator.canShare({ files: [file] })) { await navigator.share({ files: [file], text }); return; }
      if (preferWhatsApp) {
        download(file);
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
        toast.info("Image downloaded. Attach it in WhatsApp.");
      } else if (navigator.share) { await navigator.share({ text }); toast.info("This browser can't share the image. Use Download image."); }
      else toast.error("Sharing isn't supported in this browser. Use Copy details or Download image.");
    } catch (e) { if (e?.name !== "AbortError") toast.error("Could not share"); }
  };
  return (<>
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button size="sm" variant="outline" data-testid={`payin-share-${s.id}`} className="h-8 gap-1 border-slate-300 bg-white text-slate-700"><Share2 className="w-3.5 h-3.5" />Share</Button></DropdownMenuTrigger>
      <DropdownMenuContent className="bg-white w-52" align="end">
        <DropdownMenuItem data-testid="share-to-chat" onClick={() => setChat(true)} className="bg-teal-50 text-teal-800 font-semibold focus:bg-teal-100"><MessageSquare className="w-4 h-4 mr-2" />Send to Chat</DropdownMenuItem>
        <DropdownMenuItem data-testid="share-whatsapp" onClick={() => native(true)}><Smartphone className="w-4 h-4 mr-2 text-emerald-600" />WhatsApp</DropdownMenuItem>
        <DropdownMenuItem data-testid="share-other" onClick={() => native(false)}><Share2 className="w-4 h-4 mr-2" />Other apps</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="share-copy" onClick={() => navigator.clipboard.writeText(text).then(() => toast.success("Details copied"))}><Copy className="w-4 h-4 mr-2" />Copy details</DropdownMenuItem>
        <DropdownMenuItem data-testid="share-download" onClick={() => imageFile(s).then(download).catch(() => toast.error("Download failed"))}><Download className="w-4 h-4 mr-2" />Download image</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    {chat && <SendToChat s={s} text={text} onClose={() => setChat(false)} />}
  </>);
}

export function ScreenshotThumb({ s }) {
  const [open, setOpen] = useState(false);
  if (!s.screenshot_url) return <span className="text-xs text-slate-400">-</span>;
  return (<>
    <img src={fileUrl(s.screenshot_url)} alt="payment" onClick={() => setOpen(true)} data-testid={`payin-thumb-${s.id}`} className="w-9 h-9 object-cover rounded border border-slate-200 cursor-zoom-in bg-white" />
    <Dialog open={open} onOpenChange={setOpen}><DialogContent className="max-w-3xl bg-white p-2"><img src={fileUrl(s.screenshot_url)} alt="payment full" data-testid="payin-screenshot-full" className="max-h-[80vh] w-full object-contain" /></DialogContent></Dialog>
  </>);
}
